import type { JobSeverity, JobStatus } from '@/features/jobs/types/job'
import type { SlaCategory } from '@/features/reports/lib/format-sla-category'

const MS_PER_DAY = 86_400_000

// Monday. Dates before this epoch make weekday counts wrong.
const WORKING_DAY_EPOCH = Date.UTC(2000, 0, 3)

const WORKING_DAY_SEVERITIES: readonly JobSeverity[] = ['urgent', 'bau']

export const SLA_TARGET_DAYS: Record<JobSeverity, number> = {
  expedited: 7,
  urgent: 10,
  bau: 30,
}

type SlaTimed = {
  date_raised: string
  completed_at: string | null
  severity: JobSeverity
}

const utcDay = (value: string) => {
  const date = new Date(value)

  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  )
}

const weekdayIndex = (value: string) => {
  const elapsed = Math.floor((utcDay(value) - WORKING_DAY_EPOCH) / MS_PER_DAY)

  return Math.floor(elapsed / 7) * 5 + Math.min(elapsed % 7, 5)
}

export const slaUsesWorkingDays = (severity: JobSeverity) =>
  WORKING_DAY_SEVERITIES.includes(severity)

export const slaCalendarDays = (dateRaised: string, completedAt: string) =>
  Math.floor((utcDay(completedAt) - utcDay(dateRaised)) / MS_PER_DAY)

export const slaWorkingDays = (dateRaised: string, completedAt: string) =>
  weekdayIndex(completedAt) - weekdayIndex(dateRaised)

export const slaElapsedDays = (
  dateRaised: string,
  completedAt: string,
  severity: JobSeverity
) =>
  slaUsesWorkingDays(severity)
    ? slaWorkingDays(dateRaised, completedAt)
    : slaCalendarDays(dateRaised, completedAt)

export const slaExceeded = (job: SlaTimed) => {
  if (!job.completed_at) {
    return false
  }

  return (
    slaElapsedDays(job.date_raised, job.completed_at, job.severity) >
    SLA_TARGET_DAYS[job.severity]
  )
}

export const slaCategoryFor = (
  job: SlaTimed & { status: JobStatus }
): SlaCategory | null => {
  if (!job.completed_at) {
    return null
  }

  if (!slaExceeded(job)) {
    return 'within_sla'
  }

  return job.status === 'aborted' ? 'aborted_outside_sla' : 'completed_outside_sla'
}
